import Badge from '@concur/nui-widgets/Badge';
import Button from '@concur/nui-widgets/Button';
import classnamesBind from 'classnames/bind';
import PropTypes from 'prop-types';
import axios from 'axios';
import React, { useEffect, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import ReactIdleTimer from 'react-idle-timer';
import Dialog from '@concur/nui-widgets/Dialog';
import { FormattedMessage, withFormatter } from '@concur/nui-intl-runtime';
import {
    compose, withErrorBoundary, withLogger,
} from '@concur/core-ui-shell';
import { ENDPOINT_URLS } from '../constants';
import Logo from '../Logo/Logo';
import withLoggerData from '../utils/withLoggerData';
import styles from './AppIdleTimer.css';
import clearLocalizationMode from '../utils/clearLocalizationMode';
import { clearSideNavState } from '../utils/sideNavStateUtils';

const CSS_BLOCK = 'sapcnqr-app-idle-timer';
const FORCE_RESET_INTERVAL = 60_000;

// Remove unused props in the future once all teams have upgraded to min version of
// nui-shell that stops passing these props + SU251 is in production
const IdleTimer = (props) => {
    const {
        baseUrl,
        debugSessionId,
        demo,
        expirationWarningInSeconds,
        formatter,
        idleTimeoutInMinutes,
        isLoggedIn,
        sessionTimeoutInMinutes,
        logger,
        isOpusUser,
        /* eslint-disable no-unused-vars */
        logoutUser,
        pingSession,
        configOptions,
        /* eslint-enable no-unused-vars */
    } = props;

    const classnames = classnamesBind.bind(styles);

    const logoutUrl = baseUrl ? `${baseUrl}/nui-auth/api/signout?signedout=inactivity` : '/errors/loggedout.asp';

    // short circuit if either not logged in or session timeout is disabled
    if (!isLoggedIn || sessionTimeoutInMinutes <= 0) return null;

    const convertMinutesToSeconds = (minutes) => Math.floor(minutes * 60);
    const convertSecondsToMillis = (seconds) => Math.floor(seconds * 1000);
    const convertMillisToSeconds = (milliseconds) => Math.floor(milliseconds / 1000);

    // if idle timer is disabled, no prop value comes in so just use the session timeout
    let idleTimeoutInMinutesCopy = idleTimeoutInMinutes || sessionTimeoutInMinutes;

    // idleTimeoutMinutes should not be greater than sessionTimeoutMinutes
    if (idleTimeoutInMinutesCopy > sessionTimeoutInMinutes) {
        idleTimeoutInMinutesCopy = sessionTimeoutInMinutes;
    }

    // timeout props come in as minutes, but everything works off seconds and milliseconds
    const idleTimeoutInSeconds = convertMinutesToSeconds(idleTimeoutInMinutesCopy);
    const sessionTimeoutInSeconds = convertMinutesToSeconds(sessionTimeoutInMinutes);

    const idleTimeoutInMillis = convertSecondsToMillis(idleTimeoutInSeconds);
    const sessionTimeoutInMillis = convertSecondsToMillis(sessionTimeoutInSeconds);

    const countdownTimeoutInSeconds = sessionTimeoutInSeconds >= idleTimeoutInSeconds
        ? sessionTimeoutInSeconds - idleTimeoutInSeconds
        : 0;

    // let's set a keep alive delay as to not overwhelm the servers
    const keepAliveDelayInMillis = Math.floor(sessionTimeoutInMillis / 3);

    const idleTimerRef = useRef();
    const countdownTimerRef = useRef();
    const firstFocusRef = useRef(null);

    const [showDialog, setShowDialog] = useState(!!demo);
    const [idleTimerTimeout, setIdleTimerTimeout] = useState(idleTimeoutInMillis);
    const [countdownLengthInSeconds, setCountdownLengthInSeconds] = useState(countdownTimeoutInSeconds); // eslint-disable-line max-len
    const [countdownSecondsElapsed, setCountdownSecondsElapsed] = useState(0);
    const [countdownStartTime, setCountdownStartTime] = useState();
    const [lastActivityTime, setLastActivityTime] = useState(Date.now());
    const [lastKeepAlivePingTime, setLastKeepAlivePingTime] = useState(Date.now());

    const logDebugMessage = (message) => {
        if (!debugSessionId) return;

        logger.serverDebug(`${new Date()} | Debug IdleTimer (${debugSessionId}) | ${message}`);
    };

    const secondsRemaining = Math.max(countdownLengthInSeconds - countdownSecondsElapsed, 0);

    const keepSessionAlive = (forcePing) => {
        if (demo) return;

        logDebugMessage(`Keep session alive: ${forcePing}`);

        // to force a ping, rewind the last keep alive ping time
        if (forcePing) {
            setLastKeepAlivePingTime(lastKeepAlivePingTime - (keepAliveDelayInMillis + 1));
        }

        // set the state variable and let the useEffect do the processing
        setLastActivityTime(Date.now());
    };

    const onIdleTimeout = (countdownLengthInMillis) => {
        if (demo) return;

        logDebugMessage(`IDLE TIMEOUT EXPIRED: ${countdownLengthInMillis}`);

        // as a safeguard, make sure no previous countdown timer is still running
        clearInterval(countdownTimerRef.current);

        keepSessionAlive(true);

        // set state variables to trigger the countdown timer
        setCountdownLengthInSeconds(countdownLengthInMillis
            ? convertMillisToSeconds(countdownLengthInMillis)
            : countdownTimeoutInSeconds);
        setCountdownSecondsElapsed(0);
        setCountdownStartTime(Date.now());
    };

    const resetIdleTimer = (timeoutValue) => {
        if (demo) return;

        logDebugMessage(`Reset idle timer: ${timeoutValue}`);

        clearInterval(countdownTimerRef.current);
        setShowDialog(false);

        // reset idle timer
        setIdleTimerTimeout(timeoutValue || idleTimeoutInMillis);
        idleTimerRef.current.reset();

        keepSessionAlive(true);
    };

    // this function handles the onClick from the "Stay Signed In" button because that,
    // by default, sends an event parameter and when resetting the idle timer, we want
    // to use a custom parameter
    const handleResetIdleTimer = () => {
        if (demo) return;

        logDebugMessage('Handle reset idle timer');

        resetIdleTimer();
    };

    const onSessionTimeout = () => {
        if (demo) return;

        logDebugMessage('SESSION TIMEOUT EXPIRED');

        clearInterval(countdownTimerRef.current);
        setShowDialog(false);
        clearLocalizationMode();
        clearSideNavState();
        window.location.href = logoutUrl;
    };

    const onAction = (e) => {
        if (demo) return;

        logDebugMessage(`Action detected: ${e.type}`);

        keepSessionAlive();
    };

    const handleVisibilityChange = () => {
        if (demo) return;

        logDebugMessage(`Visibility change detected: ${document.hidden}`);

        if (!document.hidden) {
            const currentTime = Date.now();
            const totalElapsedTime = currentTime - (idleTimerRef.current?.getLastActiveTime() || 0);

            if (totalElapsedTime > sessionTimeoutInMillis) {
                // session timeout
                onSessionTimeout();
            } else if (totalElapsedTime > idleTimeoutInMillis) {
                // idle timeout, show countdown
                // we're not sure if the idle timer continued to run or not so let's just pause it
                // so we don't get any surprise events popping up
                idleTimerRef.current.pause();
                onIdleTimeout(sessionTimeoutInMillis - totalElapsedTime);
            } else {
                // idle timer still running, but let's reset it to make sure it's accurate
                resetIdleTimer(idleTimeoutInMillis - totalElapsedTime);
            }
        }
    };

    useEffect(() => {
        if (demo || !countdownStartTime) return;

        logDebugMessage(`Effect (countdownStartTime): ${countdownStartTime}, ${new Date(countdownStartTime)}`);

        let secondsElapsed = countdownSecondsElapsed;

        // conditionally show the modal to avoid showing it and
        // immediately dismissing it because the session timed out
        // Additionally, bypass the modal if idleTimeout is 0
        if (secondsElapsed < countdownLengthInSeconds && idleTimeoutInMinutes) {
            setShowDialog(true);
        }

        // start a new countdown timer
        const timerId = setInterval(() => {
            secondsElapsed += 1;
            setCountdownSecondsElapsed(secondsElapsed);

            keepSessionAlive();

            if (secondsElapsed >= countdownLengthInSeconds) {
                // session timeout (countdown expired)
                clearInterval(timerId);
                onSessionTimeout();
            }
        }, 1000);

        countdownTimerRef.current = timerId;

        // eslint-disable-next-line consistent-return
        return () => {
            clearInterval(countdownTimerRef.current);
        };
    }, [countdownStartTime]);

    useEffect(() => {
        if (demo) return;
        const tmpCorrelationId = uuidv4();

        logDebugMessage(`Effect (lastActivityTime): ${lastActivityTime}, ${new Date(lastActivityTime)}`);

        if (lastActivityTime > lastKeepAlivePingTime + keepAliveDelayInMillis) {
            logDebugMessage('Ping session');
            setLastKeepAlivePingTime(lastActivityTime);
            axios.get(ENDPOINT_URLS.CHECK_SESSION)
                .then((response) => response.data)
                .then((resultJson) => {
                    if (!resultJson || !resultJson.isConnected) {
                        window.location.replace(logoutUrl);
                    }
                })
                .catch((err) => {
                    logger.error(`Ping Session: ${err.message}, 'Response data: ${err.response?.data}'`, { correlationId: tmpCorrelationId });
                    // redirect user to logout if ping fails
                    window.location.replace(logoutUrl);
                });
        }
    }, [lastActivityTime]);

    useEffect(() => {
        if (demo) return;

        logDebugMessage(`Effect (debugSessionId): ${debugSessionId}`);

        // let's get a set of relevant props to debug
        const {
            demo: test1,
            logger: test4,
            formatter: test5,
            ...debugProps
        } = props;

        logDebugMessage(`Props: ${JSON.stringify(debugProps)}`);
        logDebugMessage(`State: Idle timeout (sec) = ${idleTimeoutInSeconds}, Session timeout (sec) = ${sessionTimeoutInSeconds}`);
    }, [debugSessionId]);

    // When a tab is hidden, the browser will stop intervals after a period of time.
    // Recalculate the time remaining when the tab is shown.
    useEffect(() => {
        if (demo) return;
        // check for global forceResetIdleTimer variable set by TM_ResetTimeouts() in travelManager
        const forceResetIdleTimerInterval = setInterval(() => {
            if (window?.forceResetIdleTimer) {
                logDebugMessage('Effect (forceResetIdleTimer)');
                resetIdleTimer();
                window.forceResetIdleTimer = false;
            }
        }, FORCE_RESET_INTERVAL);

        document.addEventListener('visibilitychange', handleVisibilityChange);

        // eslint-disable-next-line consistent-return
        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            clearInterval(forceResetIdleTimerInterval);
        };
    }, []);

    const timerFormatComp = ({ children }) => (
        <span
            className={classnames(
                `${CSS_BLOCK}__time-remaining`,
                {
                    [`${CSS_BLOCK}__time-remaining--expiring`]: secondsRemaining <= expirationWarningInSeconds,
                },
            )}
        >
            {children}
        </span>
    );

    return (
        <>
            {!demo && (
                <ReactIdleTimer
                    debounce={250}
                    events={[
                        'mousemove',
                        'keydown',
                        'wheel',
                        'DOMMouseScroll',
                        'mouseWheel',
                        'mousedown',
                        'touchstart',
                        'touchmove',
                        'MSPointerDown',
                        'MSPointerMove',
                    ]} // removed 'visibilitychange' from list of default events
                    onAction={onAction}
                    onIdle={onIdleTimeout}
                    ref={idleTimerRef}
                    stopOnIdle
                    timeout={idleTimerTimeout}
                />
            )}
            {debugSessionId && (
                <Badge className={classnames(`${CSS_BLOCK}__session-badge`)}>
                    Idle Timer Debug
                    <br />
                    {debugSessionId}
                </Badge>
            )}
            <Dialog
                firstFocusRef={firstFocusRef}
                backdropProps={{
                    className: classnames(`${CSS_BLOCK}__backdrop`),
                }}
                containerProps={{
                    className: classnames(`${CSS_BLOCK}__container`),
                }}
                titleContainerProps={{
                    className: classnames(`${CSS_BLOCK}__title-container`),
                }}
                className={classnames(CSS_BLOCK)}
                onHide={handleResetIdleTimer}
                show={showDialog}
                footer={{
                    ariaLabel: formatter.formattedMessage({ id: 'CoreUI.about.toolbar' }),
                    mainContent: [
                        <Button onClick={handleResetIdleTimer} ref={firstFocusRef}>
                            {formatter.formattedMessage({ id: 'CoreUI.SessionTimeout.Modal.StaySignedIn' })}
                        </Button>,
                    ],
                }}
                titleIcon={(
                    <div className={classnames(`${CSS_BLOCK}__container-logo`)}>
                        <Logo
                            className={classnames(`${CSS_BLOCK}__logo`)}
                            styles={styles}
                            cssBlock={CSS_BLOCK}
                            isOpusUser={isOpusUser}
                        />
                    </div>
                )}
                title={formatter.formattedMessage({ id: 'CoreUI.SessionTimeout.Modal.Title' })}
            >
                <p className={classnames(`${CSS_BLOCK}__paragraph`)}>
                    {formatter.formattedMessage({ id: 'CoreUI.SessionTimeout.Modal.Message1' })}
                        &nbsp;
                    {formatter.formattedMessage({ id: 'CoreUI.SessionTimeout.Modal.Message2' })}
                </p>
                <p className={classnames(`${CSS_BLOCK}__paragraph`)}>
                    <FormattedMessage
                        id="CoreUI.SessionTimeout.Modal.TimerMessage"
                        values={{
                            minutes: `${Math.floor(secondsRemaining / 60)}`,
                            seconds: `${secondsRemaining % 60}`,
                            timerFormat: timerFormatComp,
                        }}
                    />
                </p>
            </Dialog>
        </>
    );
};

IdleTimer.displayName = 'IdleTimer';

IdleTimer.propTypes = {
    baseUrl: PropTypes.string,
    demo: PropTypes.bool,
    expirationWarningInSeconds: PropTypes.number,
    idleTimeoutInMinutes: PropTypes.number, // in session as "LockTimeout"
    isLoggedIn: PropTypes.bool,
    logoutUser: PropTypes.func,
    sessionTimeoutInMinutes: PropTypes.number,
    pingSession: PropTypes.func,
    isOpusUser: PropTypes.bool,
    configOptions: PropTypes.object,
};

IdleTimer.defaultProps = {
    expirationWarningInSeconds: 30,
    sessionTimeoutInMinutes: 20,
};

export default compose(
    withFormatter,
    withErrorBoundary,
    withLoggerData,
    withLogger,
)(IdleTimer);
